import * as React from "react";

// NPM
import clsx from "clsx";
import { animated, useSpring, config } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
	useTheme,
} from "@material-ui/core/styles";
import { InputAdornment, TextField, useMediaQuery } from "@material-ui/core";

// Utility
import Utility from "../../domain/classes/Utility";

// Icons
import BackspaceIcon from "@material-ui/icons/Backspace";
import IAnimatable from "../../domain/interfaces/IAnimatable";

export const styles = (theme: Theme) =>
	createStyles({
		root: { marginBottom: theme.spacing(2), width: "100%" },
		stepper: {},
		icon: { cursor: "pointer" },
	});

export interface AppointmentTypeSearchBarProps
	extends React.HTMLAttributes<HTMLDivElement>,
		IAnimatable {
	onSearchTermChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onClearSearchTerm: () => void;
	searchTerm: string;
}

const AppointmentTypeSearchBar = React.forwardRef<
	HTMLDivElement,
	AppointmentTypeSearchBarProps & WithStyles<typeof styles>
>(
	(
		{
			classes,
			className,
			onSearchTermChange,
			onClearSearchTerm,
			searchTerm,
			hasAnimation,
			...divProps
		},
		ref
	) => {
		// Theme
		const theme = useTheme();
		const matchXs = useMediaQuery(theme.breakpoints.down("xs"));

		// Animations
		const { opacity, y } = useSpring({
			config: config.wobbly,
			opacity: 1,
			y: 0,
			from: { scale: 0.8, opacity: 0, y: -50 },
		});

		const child = (
			<TextField
				variant="outlined"
				label="Search for an appointment type"
				fullWidth
				size={matchXs ? "small" : "medium"}
				onChange={onSearchTermChange}
				value={searchTerm}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<BackspaceIcon
								color="primary"
								onClick={onClearSearchTerm}
								className={classes.icon}
							/>
						</InputAdornment>
					),
				}}
			/>
		);

		return hasAnimation ? (
			<animated.div
				className={clsx(classes.root, className)}
				{...divProps}
				ref={ref}
				style={{
					opacity,
					transform: y.interpolate((y) => `translateY(${y}px)`),
				}}
			>
				{child}
			</animated.div>
		) : (
			<div>{child}</div>
		);
	}
);

const StyledAppointmentTypeSearchBar = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("AppointmentTypeSearchBar"),
})(AppointmentTypeSearchBar);

export default StyledAppointmentTypeSearchBar;

export const AnimatedAppointmentTypeSearchBar = animated(
	StyledAppointmentTypeSearchBar
);
